import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Descriptions, 
  Tag, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Radio, 
  Timeline,
  Typography,
  Divider,
  message,
  Spin,
  Alert
} from 'antd';
import { 
  ArrowLeftOutlined, 
  CheckOutlined, 
  CloseOutlined, 
  UserOutlined,
  CalendarOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketService, Ticket, Approval } from '../services/ticketService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [approvalForm] = Form.useForm();

  useEffect(() => {
    if (id) {
      loadTicketDetail(parseInt(id));
    }
  }, [id]);

  const loadTicketDetail = async (ticketId: number) => {
    try {
      setLoading(true);
      const data = await ticketService.getTicketDetail(ticketId);
      setTicket(data);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '加载工单详情失败');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (values: any) => {
    if (!ticket || !id) return;

    try {
      setApprovalLoading(true);
      await ticketService.approveTicket(parseInt(id), {
        action: values.action,
        comment: values.comment
      });
      
      message.success(values.action === 'approve' ? '审批通过' : '审批拒绝');
      setApprovalModalVisible(false);
      approvalForm.resetFields();
      
      // 重新加载工单详情
      await loadTicketDetail(parseInt(id));
    } catch (error: any) {
      message.error(error.response?.data?.detail || '审批失败');
    } finally {
      setApprovalLoading(false);
    }
  };

  const canApprove = (ticket: Ticket) => {
    if (!user) return false;
    
    // 管理员可以审批所有工单
    if (user.is_admin) return true;
    
    // 普通用户只能审批分配给自己的工单
    return ticket.assignee_id === user.id && ticket.status === 'pending';
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      executed: 'blue',
      failed: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts = {
      pending: '待审批',
      approved: '已通过',
      rejected: '已拒绝',
      executed: '已执行',
      failed: '执行失败'
    };
    return texts[status as keyof typeof texts] || status;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'blue',
      normal: 'default',
      high: 'orange',
      urgent: 'red'
    };
    return colors[priority as keyof typeof colors] || 'default';
  };

  const getPriorityText = (priority: string) => {
    const texts = {
      low: '低',
      normal: '普通',
      high: '高',
      urgent: '紧急'
    };
    return texts[priority as keyof typeof texts] || priority;
  };

  const getApprovalIcon = (action: string) => {
    return action === 'approve' ? 
      <CheckOutlined style={{ color: '#52c41a' }} /> : 
      <CloseOutlined style={{ color: '#f5222d' }} />;
  };

  const getApprovalText = (action: string) => {
    return action === 'approve' ? '审批通过' : '审批拒绝';
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="page-container">
        <Alert message="工单不存在或无权限访问" type="error" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/tickets')}
            >
              返回列表
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              工单详情
            </Title>
            <Tag color="blue">{ticket.dms_ticket_code}</Tag>
          </Space>
        </div>

        <Descriptions
          title={ticket.title}
          bordered
          column={{ xs: 1, sm: 2, md: 2 }}
          extra={
            <Space>
              <Tag color={getStatusColor(ticket.status)} style={{ fontSize: 14 }}>
                {getStatusText(ticket.status)}
              </Tag>
              <Tag color={getPriorityColor(ticket.priority)}>
                {getPriorityText(ticket.priority)}
              </Tag>
              {canApprove(ticket) && (
                <Button
                  type="primary"
                  onClick={() => setApprovalModalVisible(true)}
                  disabled={ticket.status !== 'pending'}
                >
                  审批工单
                </Button>
              )}
            </Space>
          }
        >
          <Descriptions.Item label="工单描述" span={2}>
            <div className="ticket-description" dangerouslySetInnerHTML={{
              __html: ticket.description ? 
                ticket.description.replace(/```sql\n([\s\S]*?)\n```/g, '<pre><code>$1</code></pre>') :
                '暂无描述'
            }} />
          </Descriptions.Item>
          
          <Descriptions.Item label={<><UserOutlined /> 提交人</>}>
            {ticket.submitter?.name || ticket.submitter_name}
            {ticket.submitter?.department && (
              <Text type="secondary"> ({ticket.submitter.department})</Text>
            )}
          </Descriptions.Item>
          
          <Descriptions.Item label="审批人">
            {ticket.assignee?.name || '未分配'}
            {ticket.assignee?.department && (
              <Text type="secondary"> ({ticket.assignee.department})</Text>
            )}
          </Descriptions.Item>
          
          <Descriptions.Item label={<><DatabaseOutlined /> 数据库</>}>
            {ticket.database_name || '未指定'}
          </Descriptions.Item>
          
          <Descriptions.Item label="事件类型">
            {ticket.event_type || '未知'}
          </Descriptions.Item>
          
          <Descriptions.Item label={<><CalendarOutlined /> 创建时间</>}>
            {new Date(ticket.created_at).toLocaleString()}
          </Descriptions.Item>
          
          <Descriptions.Item label="更新时间">
            {new Date(ticket.updated_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>



        {ticket.approvals && ticket.approvals.length > 0 && (
          <>
            <Divider orientation="left">审批历史</Divider>
            <Timeline>
              {ticket.approvals.map((approval: Approval) => (
                <Timeline.Item
                  key={approval.id}
                  dot={getApprovalIcon(approval.action)}
                >
                  <div>
                    <Text strong>
                      {approval.approver?.name} {getApprovalText(approval.action)}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {new Date(approval.created_at).toLocaleString()}
                    </Text>
                    {approval.comment && (
                      <>
                        <br />
                        <Text>{approval.comment}</Text>
                      </>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </>
        )}
      </Card>

      {/* 审批模态框 */}
      <Modal
        title="工单审批"
        open={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false);
          approvalForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={approvalForm}
          layout="vertical"
          onFinish={handleApproval}
          initialValues={{ action: 'approve' }}
        >
          <Form.Item
            name="action"
            label="审批决定"
            rules={[{ required: true, message: '请选择审批决定' }]}
          >
            <Radio.Group>
              <Radio.Button value="approve" style={{ color: '#52c41a' }}>
                <CheckOutlined /> 通过
              </Radio.Button>
              <Radio.Button value="reject" style={{ color: '#f5222d' }}>
                <CloseOutlined /> 拒绝
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="comment"
            label="审批意见"
            rules={[{ max: 500, message: '审批意见不能超过500字符' }]}
          >
            <TextArea
              rows={4}
              placeholder="请输入审批意见（可选）"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => {
                  setApprovalModalVisible(false);
                  approvalForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={approvalLoading}
              >
                确认提交
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TicketDetail;

